import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { ChevronLeft, ChevronRight, Download, FileText, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { getForumAttachment } from "../../services/moodle/forum";
import { attachmentExtension, formatFileSize } from "./attachmentUtils";
import { OfficePreview } from "./OfficePreview";
import { previewHeaderTheme } from "./previewHeaderTheme";
import { FittedImage } from "./FittedImage";
import "./attachments.css";

const mimeFor = (extension) => ({ jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", pdf: "application/pdf" }[extension] || "application/octet-stream");

const usePrivateFile = (file, token) => {
  const [state, setState] = useState({ loading: false });
  useEffect(() => {
    if (!file || !token) {
      setState({ loading: false, error: file ? "Inicia sesión para ver el archivo." : "" });
      return undefined;
    }
    const controller = new AbortController();
    let objectUrl;
    let active = true;
    setState({ loading: true });
    getForumAttachment({ token, fileurl: file.fileurl, signal: controller.signal })
      .then((blob) => {
        if (!active) return;
        const safeBlob = new Blob([blob], { type: mimeFor(attachmentExtension(file)) });
        objectUrl = URL.createObjectURL(safeBlob);
        setState({ loading: false, url: objectUrl, blob, file, token });
      })
      .catch((error) => { if (active) setState({ loading: false, error: error.message }); });
    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, token]);
  // Do not show the previous file's bytes during the next request's first render.
  return state.blob && (state.file !== file || state.token !== token) ? { loading: Boolean(file && token) } : state;
};

const Thumbnail = ({ file, token }) => {
  const image = ["jpg", "jpeg", "png"].includes(attachmentExtension(file));
  const { url } = usePrivateFile(image ? file : null, token);
  return url ? <img src={url} alt="" loading="lazy" /> : <FileText size={30} aria-hidden="true" />;
};

/** Bounded read-only CSV preview, including quoted separators and escaped quotes. */
export const parseCsvPreview = (text, maxRows = 200, maxColumns = 50) => {
  const source = text.replace(/^\uFEFF/, "");
  const first = source.split(/\r?\n/, 1)[0];
  const separator = (first.match(/;/g) || []).length > (first.match(/,/g) || []).length ? ";" : ",";
  const rows = [];
  let row = [], value = "", quoted = false;
  for (let i = 0; i < source.length && rows.length < maxRows; i += 1) {
    const char = source[i];
    if (char === '"') {
      if (quoted && source[i + 1] === '"') { value += '"'; i += 1; }
      else quoted = !quoted;
    } else if (!quoted && (char === separator || char === "\n" || char === "\r")) {
      if (row.length < maxColumns) row.push(value);
      value = "";
      if (char !== separator) {
        rows.push(row); row = [];
        if (char === "\r" && source[i + 1] === "\n") i += 1;
      }
    } else { value += char; }
  }
  if ((row.length || value) && rows.length < maxRows) {
    if (row.length < maxColumns) row.push(value);
    rows.push(row);
  }
  return rows;
};

export const CsvPreview = ({ blob }) => {
  const [rows, setRows] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    // Only decode a bounded slice; the original is always available for download.
    blob.slice(0, 1024 * 1024).text().then((text) => {
      if (active) {
        const parsed = parseCsvPreview(text, 201, 51);
        setTruncated(blob.size > 1024 * 1024 || parsed.length > 200 || parsed.some((row) => row.length > 50));
        setRows(parsed.slice(0, 200).map((row) => row.slice(0, 50)));
      }
    }).catch(() => { if (active) setError("No se pudo leer la vista previa del CSV."); });
    return () => { active = false; };
  }, [blob]);
  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p role="status">Preparando tabla…</p>;
  return <div className="forum-csv-preview">
    {truncated && <p role="status">Vista previa recortada: se muestran hasta 200 filas y 50 columnas del primer MB.</p>}
    <div className="forum-data-table" role="region" aria-label="Vista previa CSV" tabIndex={0}>
      <table aria-label="Vista previa CSV"><tbody>{rows.map((row, index) => <tr key={index}>{row.map((value, column) => <td key={column}>{value}</td>)}</tr>)}</tbody></table>
    </div>
  </div>;
};

export const AttachmentGallery = ({ files = [] }) => {
  const auth = useAuth();
  const theme = useTheme();
  const token = auth?.user?.token;
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [sheetSelectorTarget, setSheetSelectorTarget] = useState(null);
  const file = selected === null ? null : files[selected];
  const state = usePrivateFile(file, token);
  const extension = file ? attachmentExtension(file) : "";
  const close = () => setSelected(null);
  const navigate = (direction) => { setZoom(1); setSelected((selected + direction + files.length) % files.length); };
  useEffect(() => { setZoom(1); }, [file]);
  if (!files.length) return null;
  return <>
    <div className="forum-attachment-grid" data-theme={theme.mode === "dark" ? "dark" : "light"} aria-label="Archivos adjuntos">
      {files.map((attachment, index) => <button type="button" className="forum-attachment-card"
        key={`${attachment.fileurl}-${index}`} onClick={() => setSelected(index)} aria-label={`Ver ${attachment.filename}`}>
        <div className="forum-attachment-thumbnail"><Thumbnail file={attachment} token={token} /></div>
        <strong>{attachment.filename}</strong><small>{formatFileSize(attachment.filesize)}</small>
      </button>)}
    </div>
    <Dialog open={Boolean(file)} onClose={close} fullWidth maxWidth="lg" aria-labelledby="forum-attachment-title"
      PaperProps={{ className: "forum-attachment-dialog" }}>
      {file && <>
        <header className="forum-attachment-toolbar" style={previewHeaderTheme(theme)}>
          <div><strong id="forum-attachment-title" title={file.filename}>{file.filename}</strong></div>
          {state.url && <a href={state.url} download={file.filename} aria-label="Descargar archivo"><Download size={20} /></a>}
          <button type="button" onClick={close} aria-label="Cerrar vista previa"><X size={20} /></button>
        </header>
        <div className="forum-attachment-preview">
          {state.loading && <p role="status">Cargando archivo…</p>}
          {state.error && <p role="alert">{state.error}</p>}
          {state.url && ["jpg", "jpeg", "png"].includes(extension) && <FittedImage src={state.url} alt={file.filename} zoom={zoom} />}
          {state.url && extension === "pdf" && <iframe title={`Vista previa de ${file.filename}`} src={state.url} sandbox="allow-scripts" />}
          {state.blob && extension === "csv" && <CsvPreview key={file.fileurl} blob={state.blob} />}
          {state.blob && ["docx", "xlsx"].includes(extension) && <OfficePreview key={file.fileurl} blob={state.blob} extension={extension} filename={file.filename} sheetSelectorTarget={sheetSelectorTarget} />}
        </div>
        <div className="forum-attachment-controls">
          <div className="forum-sheet-selector-slot" ref={setSheetSelectorTarget} />
          <div className="forum-attachment-navigation">
          <button type="button" onClick={() => navigate(-1)} disabled={files.length < 2} aria-label="Adjunto anterior"><ChevronLeft /></button>
          {["jpg", "jpeg", "png"].includes(extension) && <>
            <button type="button" onClick={() => setZoom(Math.max(0.5, zoom - 0.25))} aria-label="Reducir imagen">−</button>
            <span>{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => setZoom(Math.min(3, zoom + 0.25))} aria-label="Ampliar imagen">+</button>
          </>}
          <button type="button" onClick={() => navigate(1)} disabled={files.length < 2} aria-label="Adjunto siguiente"><ChevronRight /></button>
          </div>
          <span className="forum-attachment-meta">{formatFileSize(file.filesize)} · Archivo {selected + 1} de {files.length}</span>
        </div>
      </>}
    </Dialog>
  </>;
};
